import { useCallback, useEffect, useRef, useState } from "react";
import { Undo2, Redo2, Trash2, Save } from "lucide-react";
import { asset } from "../../lib/api";
import { strokeSeg, stamp, floodFill } from "./tools";
import { Toolbar } from "./Toolbar";

const SIZE = 1024;
const MAX_HISTORY = 15;

const useLineMask = (lineUrl, mode) => {
  const mask = useRef(null);
  const img = useRef(null);
  useEffect(() => {
    mask.current = null;
    img.current = null;
    if (!lineUrl) return;
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      img.current = im;
      if (mode !== "color") return;
      const c = document.createElement("canvas");
      c.width = c.height = SIZE;
      const x = c.getContext("2d");
      x.drawImage(im, 0, 0, SIZE, SIZE);
      mask.current = x.getImageData(0, 0, SIZE, SIZE).data;
    };
    im.src = asset(lineUrl);
  }, [lineUrl, mode]);
  return [mask, img];
};

const ActionBtn = ({ testid, onClick, disabled, children, label, tone = "bg-white text-stone-700" }) => (
  <button data-testid={testid} onClick={onClick} disabled={disabled} title={label}
    className={`h-14 min-w-14 px-3 rounded-2xl border-2 border-stone-200 flex items-center justify-center gap-2 font-bold text-lg disabled:opacity-40 active:scale-95 transition-transform ${tone}`}>
    {children}<span className="hidden lg:inline">{label}</span>
  </button>
);

export const DrawingBoard = ({ lineUrl, mode = "free", onSave, saving }) => {
  const canvas = useRef(null);
  const live = useRef(null);
  const [mask, lineImg] = useLineMask(lineUrl, mode);
  const [tool, setTool] = useState(mode === "color" ? "fill" : "crayon");
  const [color, setColor] = useState("#E53935");
  const [size, setSize] = useState(18);
  const [sticker, setSticker] = useState("⭐");
  const hist = useRef({ past: [], future: [] });
  const [, force] = useState(0);
  const last = useRef(null);
  const dirty = useRef(false);

  const ctx = () => canvas.current.getContext("2d", { willReadFrequently: true });
  const snapshot = () => {
    const h = hist.current;
    h.past.push(ctx().getImageData(0, 0, SIZE, SIZE));
    if (h.past.length > MAX_HISTORY) h.past.shift();
    h.future = [];
    force((n) => n + 1);
  };
  const reset = useCallback(() => {
    const c = canvas.current.getContext("2d");
    c.fillStyle = "#FFFFFF";
    c.fillRect(0, 0, SIZE, SIZE);
  }, []);
  useEffect(() => { reset(); hist.current = { past: [], future: [] }; }, [reset, lineUrl]);

  const pos = (e) => {
    const r = canvas.current.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * SIZE, y: ((e.clientY - r.top) / r.height) * SIZE };
  };
  const down = (e) => {
    e.preventDefault();
    const p = pos(e);
    snapshot();
    dirty.current = true;
    if (tool === "fill") { floodFill(ctx(), p, color, mask.current); return; }
    if (tool === "sticker") { stamp(ctx(), sticker, size * 4 + 40, p); return; }
    canvas.current.setPointerCapture(e.pointerId);
    last.current = p;
    strokeSeg(target(), tool === "marker" ? "markerLive" : tool, color, size, p, { x: p.x + 0.1, y: p.y + 0.1 });
  };
  const target = () => (tool === "marker" ? live.current.getContext("2d") : ctx());
  const move = (e) => {
    if (!last.current) return;
    const p = pos(e);
    strokeSeg(target(), tool === "marker" ? "markerLive" : tool, color, size, last.current, p);
    last.current = p;
  };
  const up = () => {
    if (last.current && tool === "marker") {
      const c = ctx();
      c.save(); c.globalAlpha = 0.55; c.drawImage(live.current, 0, 0); c.restore();
      live.current.getContext("2d").clearRect(0, 0, SIZE, SIZE);
    }
    last.current = null;
  };

  const undo = () => {
    const h = hist.current;
    if (!h.past.length) return;
    h.future.push(ctx().getImageData(0, 0, SIZE, SIZE));
    ctx().putImageData(h.past.pop(), 0, 0);
    force((n) => n + 1);
  };
  const redo = () => {
    const h = hist.current;
    if (!h.future.length) return;
    h.past.push(ctx().getImageData(0, 0, SIZE, SIZE));
    ctx().putImageData(h.future.pop(), 0, 0);
    force((n) => n + 1);
  };
  const clear = () => { snapshot(); reset(); };
  const save = () => {
    const out = document.createElement("canvas");
    out.width = out.height = SIZE;
    const o = out.getContext("2d");
    o.drawImage(canvas.current, 0, 0);
    if (mode === "color" && lineImg.current) o.drawImage(lineImg.current, 0, 0, SIZE, SIZE);
    out.toBlob((b) => onSave(b), "image/png");
  };

  return (
    <div className="flex flex-col md:flex-row gap-4 items-center md:items-start" data-testid="drawing-board">
      <Toolbar tool={tool} setTool={setTool} color={color} setColor={setColor} size={size} setSize={setSize} sticker={sticker} setSticker={setSticker} />
      <div className="flex flex-col items-center gap-3">
        <div className="relative rounded-3xl overflow-hidden border-4 border-white shadow-xl bg-white w-[min(calc(100vw-2rem),62vh)] h-[min(calc(100vw-2rem),62vh)] md:w-[min(calc(100vw-20rem),72vh)] md:h-[min(calc(100vw-20rem),72vh)]">
          <canvas ref={canvas} width={SIZE} height={SIZE} data-testid="drawing-canvas" className="absolute inset-0 w-full h-full touch-none cursor-crosshair"
            onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up} />
          <canvas ref={live} width={SIZE} height={SIZE} className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.55 }} />
          {lineUrl && <img src={asset(lineUrl)} alt="" data-testid="drawing-line-overlay" className="absolute inset-0 w-full h-full pointer-events-none select-none" style={{ opacity: mode === "color" ? 1 : 0.22 }} />}
        </div>
        <div className="flex gap-2 flex-wrap justify-center">
          <ActionBtn testid="draw-undo-btn" onClick={undo} disabled={!hist.current.past.length} label="上一步"><Undo2 /></ActionBtn>
          <ActionBtn testid="draw-redo-btn" onClick={redo} disabled={!hist.current.future.length} label="下一步"><Redo2 /></ActionBtn>
          <ActionBtn testid="draw-clear-btn" onClick={clear} label="全部清掉"><Trash2 /></ActionBtn>
          <ActionBtn testid="draw-save-btn" onClick={save} disabled={saving} label="完成作品" tone="bg-[#E2683C] text-white border-[#E2683C]"><Save /><span className="lg:hidden">完成</span></ActionBtn>
        </div>
      </div>
    </div>
  );
};
